import { useCallback, useEffect, useMemo, useState } from 'react';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { getStripePromise, resetStripePromise } from '../lib/stripe';
import { createCheckout } from '../lib/api';
import { useCurrency } from '../contexts/CurrencyContext';
import { resolveCheckoutClientSecret, trackPaymentStarted } from '../lib/paymentAnalytics';

interface PaywallModalProps {
  sessionId: string;
  partnerToken: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (reason: 'payment-completed' | 'already-paid') => void;
}

export default function PaywallModal({
  sessionId,
  partnerToken,
  isOpen,
  onClose,
  onComplete,
}: PaywallModalProps) {
  const { currency } = useCurrency();
  const [error, setError] = useState<string | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState(false);

  const attemptLoadStripe = useCallback(async () => {
    setStripeLoading(true);
    setStripeError(false);
    try {
      const instance = await getStripePromise();
      if (!instance) throw new Error('Stripe instance is null');
      setStripe(instance);
    } catch {
      setStripeError(true);
    } finally {
      setStripeLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && !stripe && !stripeLoading && !stripeError) {
      attemptLoadStripe();
    }
  }, [isOpen, stripe, stripeLoading, stripeError, attemptLoadStripe]);

  const handleRetryStripe = useCallback(() => {
    resetStripePromise();
    attemptLoadStripe();
  }, [attemptLoadStripe]);

  const fetchClientSecret = useCallback(async () => {
    const result = await createCheckout({
      sessionId,
      partnerToken,
      currency: currency.toLowerCase(),
    });

    const clientSecret = resolveCheckoutClientSecret(result, () => {
      onClose();
      onComplete('already-paid');
    });
    trackPaymentStarted();

    return clientSecret;
  }, [sessionId, partnerToken, currency, onClose, onComplete]);

  const handleEmbeddedComplete = useCallback(
    () => onComplete('payment-completed'),
    [onComplete],
  );

  const checkoutOptions = useMemo(
    () => ({ fetchClientSecret, onComplete: handleEmbeddedComplete }),
    [fetchClientSecret, handleEmbeddedComplete],
  );

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center transition-opacity duration-200 ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      aria-hidden={!isOpen}
      // @ts-expect-error inert is a valid HTML attribute but not yet typed in React
      inert={!isOpen ? '' : undefined}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl bg-white shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        {stripeError ? (
          <div className="p-8 text-center space-y-4">
            <p className="text-red-500 text-sm">
              Unable to load the payment system. This can happen if you have an
              ad blocker enabled or if your internet connection is unstable.
            </p>
            <p className="text-slate-500 text-xs">
              Try disabling your ad blocker, check your connection, then tap
              below to retry.
            </p>
            <button
              onClick={handleRetryStripe}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
            >
              Try again
            </button>
          </div>
        ) : !stripe ? (
          <div className="p-8 flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
          </div>
        ) : error ? (
          <div className="p-8 text-center space-y-4">
            <p className="text-red-500 text-sm">{error}</p>
            <button
              onClick={() => setError(null)}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
            >
              Try again
            </button>
          </div>
        ) : (
          <div className="p-1">
            <EmbeddedCheckoutProvider stripe={stripe} options={checkoutOptions}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        )}
      </div>
    </div>
  );
}
